import { useHistory } from '@fs/zion-router'
import { Row, H5 } from '@fs/zion-ui'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePageView } from '@fs/zion-analytics'
import { usePageTitle } from '../app/PageTitleContext'
import useNotifications from '../../hooks/useNotifications'
import { getArtifactGroups, getArtifacts } from '../../services/artifactService'
import { STANDARDS_DAY_MONTH_YEAR_FORMAT, convertDateString, toLocaleString } from '../../util/DateUtil'
import { DmcDatePicker } from '../common/DmcDatePicker/DmcDatePicker'
import ArtifactCard from './ArtifactCard'
import ArtifactCardGrid from './ArtifactCardGrid'
import CategoryFilter from './filter/CategoryFilter'
import LanguageFilter from './filter/LanguageFilter'
import { DEFAULT_PAGE_SIZE, LANGUAGE_PREF_KEY } from './util/artifactsConstants'
import { getYesterdaysDate } from './util/artifactsUtils'
import DmcPaginator from '../common/DmcPaginator'

/**
 * Page that displays artifacts uploaded on a given date. Has upload date selector,
 * category filter, pagination, and page size selector. Used to screen recently-uploaded
 * artifacts for categories (e.g. sacred) that WebPurify doesn’t screen for.
 */
export default function RecentArtifacts() {
  usePageView({ page_detail: 'DMConsole:RecentArtifacts' })
  const MAX_RESULTS = 500
  const history = useHistory()
  const searchParams = new URLSearchParams(history.location.search)
  const category = searchParams.get('category') || 'any'
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.artifacts.recent'))
  const language = searchParams.get('language') || localStorage.getItem(LANGUAGE_PREF_KEY) || 'all'
  const uploadDate = searchParams.get('uploadDate')
  const [recentArtifacts, setRecentArtifacts] = useState()
  const [totalArtifacts, setTotalArtifacts] = useState(0)
  const [index, setIndex] = useState(0)
  const [pageCount, setPageCount] = useState(DEFAULT_PAGE_SIZE)
  const [dateItem, setDateItem] = useState({
    key: 'recentArtifactDate',
    primaryText: uploadDate
      ? convertDateString(uploadDate, 'yyyy-MM-DD', STANDARDS_DAY_MONTH_YEAR_FORMAT)
      : toLocaleString(getYesterdaysDate(), false),
    formattedDate: uploadDate || convertDateString(getYesterdaysDate(), null, 'yyyy-MM-DD'),
  })
  const [shouldRefetch, setShouldRefetch] = useState(true)
  const refetch = () => setShouldRefetch(!shouldRefetch)
  const { error } = useNotifications()

  useEffect(() => {
    let isCurrent = true
    getArtifacts(dateItem?.formattedDate, category, pageCount, index * pageCount, language)
      .then(async (response) => {
        if (isCurrent) {
          setRecentArtifacts(response.data.artifact)
          setTotalArtifacts(
            response.data.totalArtifactsSpanningPages === 1
              ? response.data.totalArtifactsSpanningPages + 1
              : response.data.totalArtifactsSpanningPages
          )
        }
        const artifactsWithGroups = await Promise.allSettled(
          response.data.artifact.map(async (artifact) => {
            if (artifact.visibility === 'PRIVATE') {
              await getArtifactGroups(artifact.id)
                .then((groupResponse) => {
                  artifact.groupIds = groupResponse.data.groupIds
                })
                .catch((err) => {
                  err.response?.status !== 403 &&
                    error(
                      t(
                        'artifacts.error.getArtifactGroups',
                        'Error getting artifact groups for artifact {artifactId}',
                        {
                          artifactId: artifact.id,
                        }
                      ),
                      err
                    )
                })
            }
            return artifact
          })
        )
        setRecentArtifacts(artifactsWithGroups.map((a) => a.value))
      })
      .catch((err) => {
        if (isCurrent) {
          error(t('artifacts.error.gettingRecentArtifacts', 'Error getting recent artifacts'), err)
          setRecentArtifacts([])
          setTotalArtifacts(0)
        }
      })
    return () => {
      isCurrent = false
    }
  }, [index, pageCount, category, language, error, dateItem?.formattedDate, t, shouldRefetch])

  useEffect(() => {
    window.scrollTo({ top: 0, insetInlineStart: 0, behavior: 'smooth' })
  }, [index])

  if (!history.location.search) {
    history.push(`/artifacts/recent?uploadDate=${dateItem.formattedDate}&category=any&language=${language}`)
  }

  const onLanguageChange = (newLanguage) => {
    localStorage.setItem(LANGUAGE_PREF_KEY, newLanguage)
    history.push(`/artifacts/recent?uploadDate=${dateItem?.formattedDate}&category=${category}&language=${newLanguage}`)
  }
  const onDateChange = (newDateItem) => {
    if (newDateItem !== null && newDateItem.formattedDate && newDateItem.formattedDate !== dateItem.formattedDate) {
      setRecentArtifacts()
      setIndex(0)
      setDateItem(newDateItem)
      history.push(
        `/artifacts/recent?uploadDate=${newDateItem?.formattedDate}&category=${category}&language=${language}`
      )
    }
  }

  const onCategoryChange = (newCategory) => {
    history.push(`/artifacts/recent?uploadDate=${dateItem?.formattedDate}&category=${newCategory}&language=${language}`)
  }

  let artifactGrid
  if (recentArtifacts && recentArtifacts.length === 0) {
    artifactGrid = <H5 supplementary>{t('artifacts.noArtifactsFound', 'No artifacts found')}</H5>
  } else if (recentArtifacts && recentArtifacts.length > 0) {
    artifactGrid = (
      <DmcPaginator
        columns={12}
        maxPageCount={MAX_RESULTS}
        length={totalArtifacts}
        pageCount={pageCount}
        setPageCount={setPageCount}
        index={index}
        setIndex={setIndex}
        topAndBottom
      >
        <ArtifactCardGrid>
          {recentArtifacts.map((artifact) => {
            return artifact.first ? (
              <ArtifactCard key={artifact.id} artifact={artifact} refetchArtifacts={refetch} />
            ) : null
          })}
        </ArtifactCardGrid>
      </DmcPaginator>
    )
  }

  return (
    <>
      <Row gutters="nano">
        <DmcDatePicker selectedItem={dateItem} setSelectedItem={onDateChange} dayMonthYearDateFormat="yyyy-MM-DD" />
        <span growX />
        {category && <CategoryFilter value={category} onChange={onCategoryChange} />}
        {language && <LanguageFilter value={language} onChange={onLanguageChange} />}
      </Row>
      {artifactGrid}
    </>
  )
}
